import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects';
import { useProjectStore } from '../../contexts/projectStoreProvider';

// Define project status type
type ProjectStatus = 'Completed' | 'Cancelled' | 'On Hold';

// Define archived project interface
interface ArchivedProject {
  id: string;
  name: string;
  description: string;
  organization: string;
  status: ProjectStatus;
  created_at: string;
  completed_at: string;
  scheduleCount: number;
}

const Archive: React.FC = () => {
  const { projects, updateProject } = useProjects();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'All'>('All');
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'organization'>('recent');
  const [isRestoring, setIsRestoring] = useState<string | null>(null);
  
  // Get archived projects from the store
  const archivedProjects = projects.filter(project => 
    project.status === 'Completed' || 
    project.status === 'Cancelled' || 
    project.status === 'On Hold'
  ).map(project => ({
    id: project.id,
    name: project.name,
    description: project.description || '',
    organization: project.organization,
    status: project.status as ProjectStatus,
    created_at: project.created_at,
    completed_at: project.updated_at,
    scheduleCount: project.schedules?.length || 0
  }));
  
  // Filter projects based on search query and status filter
  const filteredProjects = archivedProjects
    .filter(project => 
      searchQuery === '' || 
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.organization.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter(project => statusFilter === 'All' || project.status === statusFilter);
  
  // Sort projects
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (sortBy === 'recent') {
      return new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime();
    } else if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    } else if (sortBy === 'organization') {
      return a.organization.localeCompare(b.organization);
    }
    return 0;
  });
  
  // Get status badge color
  const getStatusColor = (status: ProjectStatus): string => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'On Hold': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Handle restoring a project
  const handleRestoreProject = async (projectId: string) => {
    setIsRestoring(projectId);
    
    try {
      await updateProject(projectId, { status: 'Active' });
      // Success - no need to do anything as the project will disappear from the filtered list
    } catch (error) {
      console.error('Failed to restore project:', error);
    } finally {
      setIsRestoring(null);
    }
  };
  
  // Handle viewing a project
  const handleViewProject = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  return (
    <div className="p-6">
      {/* Breadcrumb navigation */}
      <div className="mb-4 flex items-center text-sm text-gray-500">
        <Link to="/projects" className="hover:text-[#nb-accent-primary]">Projects</Link>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="font-medium text-gray-900">Archive</span>
      </div>
      
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Project Archive</h1>
          <p className="text-gray-500">
            View and access completed or inactive projects
          </p>
        </div>
        <div className="flex space-x-3">
          <button 
            className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export List
          </button>
        </div>
      </div>
      
      {/* Search and filter */}
      <div className="mb-6 flex justify-between">
        <div className="relative w-64">
          <input 
            type="text" 
            placeholder="Search archived projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#nb-accent-primary] focus:border-transparent"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <div className="flex space-x-2">
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | 'All')}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#nb-accent-primary] focus:border-transparent"
          >
            <option value="All">All statuses</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
            <option value="On Hold">On Hold</option>
          </select>
          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'recent' | 'name' | 'organization')}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#nb-accent-primary] focus:border-transparent"
          >
            <option value="recent">Sort by: Recently completed</option>
            <option value="name">Sort by: Name</option>
            <option value="organization">Sort by: Organization</option>
          </select>
        </div>
      </div>
      
      {/* Projects table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Organization
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Completion Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Schedules
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedProjects.length > 0 ? (
              sortedProjects.map(project => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{project.name}</div>
                        <div className="text-sm text-gray-500 line-clamp-1">{project.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{project.organization}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(project.completed_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {project.scheduleCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      onClick={() => handleViewProject(project.id)}
                      className="text-[#nb-accent-primary] hover:text-[#nb-accent-blue] mr-4"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => handleRestoreProject(project.id)}
                      disabled={isRestoring === project.id}
                      className={`text-gray-600 hover:text-gray-900 ${isRestoring === project.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isRestoring === project.id ? 'Restoring...' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-700 mb-1">No Archived Projects Found</h3>
                  <p className="text-gray-500">
                    {searchQuery 
                      ? `No projects match your search for "${searchQuery}".` 
                      : statusFilter !== 'All' 
                        ? `No ${statusFilter} projects found.` 
                        : 'No archived projects found. Completed, cancelled, or on-hold projects will appear here.'
                    }
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Archive; 

